// ===== CHRONOS AI - ANALIZADOR DE DOCUMENTOS BÁSICO =====
// Sistema de extracción de metadata desde nombres de archivo y fechas de upload

/**
 * Extrae fecha del nombre de archivo usando múltiples patrones
 */
export function extractDateFromFilename(filename) {
    if (!filename) return null;

    // Patrones de fecha comunes en nombres de archivo
    const patterns = [
        // YYYY-MM-DD
        /(\d{4})[-_](\d{2})[-_](\d{2})/,
        // DD-MM-YYYY o DD/MM/YYYY
        /(\d{2})[-_\/](\d{2})[-_\/](\d{4})/,
        // YYYYMMDD
        /(\d{4})(\d{2})(\d{2})/,
        // DD.MM.YYYY
        /(\d{2})\.(\d{2})\.(\d{4})/
    ];

    for (const pattern of patterns) {
        const match = filename.match(pattern);
        if (match) {
            let year, month, day;

            if (match[1].length === 4) {
                // Formato YYYY-MM-DD
                year = parseInt(match[1]);
                month = parseInt(match[2]);
                day = parseInt(match[3]);
            } else {
                // Formato DD-MM-YYYY
                day = parseInt(match[1]);
                month = parseInt(match[2]);
                year = parseInt(match[3]);
            }

            // Validar fecha
            if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return new Date(year, month - 1, day);
            }
        }
    }

    return null;
}

/**
 * Clasifica el tipo de documento basado en palabras clave en el nombre
 */
export function classifyDocumentType(filename) {
    if (!filename) return 'document';

    const lowerName = filename.toLowerCase();

    // Diccionario de términos legales Costa Rica
    const classifications = {
        'demanda': ['demanda', 'demand'],
        'sentencia': ['sentencia', 'fallo', 'judgment'],
        'resolucion': ['resolucion', 'resolution', 'auto'],
        'notificacion': ['notificacion', 'notif', 'edicto'],
        'prueba': ['prueba', 'evidence', 'testigo', 'perito'],
        'audiencia': ['audiencia', 'hearing', 'comparecencia'],
        'escrito': ['escrito', 'memorial', 'alegato'],
        'apelacion': ['apelacion', 'appeal', 'recurso'],
        'hecho_generador': ['hecho generador', 'hecho', 'incidente']
    };

    for (const [type, keywords] of Object.entries(classifications)) {
        for (const keyword of keywords) {
            if (lowerName.includes(keyword)) {
                return type;
            }
        }
    }

    // Clasificar por extensión como fallback
    if (lowerName.endsWith('.pdf')) return 'document';
    if (lowerName.match(/\.(jpg|jpeg|png|gif)$/)) return 'prueba';
    if (lowerName.match(/\.(mp3|wav|m4a)$/)) return 'audiencia';

    return 'document';
}

/**
 * Extrae confianza de la clasificación (0-100)
 */
export function getClassificationConfidence(filename, documentType) {
    if (!filename || documentType === 'document') return 30;

    const lowerName = filename.toLowerCase();

    // Confianza alta si palabra clave exacta
    if (lowerName.includes(documentType.replace('_', ' '))) return 95;

    // Confianza media si hay palabra relacionada
    const relatedTerms = {
        'demanda': ['demand', 'querella'],
        'sentencia': ['fallo', 'judgment'],
        'resolucion': ['auto', 'acuerdo'],
        'notificacion': ['notif', 'edicto']
    };

    const terms = relatedTerms[documentType] || [];
    for (const term of terms) {
        if (lowerName.includes(term)) return 75;
    }

    return 50;
}

/**
 * Analiza un documento completo y retorna metadata enriquecida
 */
/**
 * Analiza un documento completo y retorna metadata enriquecida (CEREBRO UNIVERSAL)
 * Intenta usar Gemini primero, luego fallback a metadata.
 */
export async function analyzeDocument(driveFile, apiKey = null, token = null) {
    const filename = driveFile.name || driveFile.driveFileName || '';

    // 1. INTENTO DE ANÁLISIS PROFUNDO (GEMINI)
    if (apiKey && (driveFile.driveFileLink || driveFile.webViewLink)) {
        try {
            // Dynamic import to avoid circular dependency issues if not handled carefully
            const { analyzeScannedImageWithGemini } = await import('./geminiAnalyzer.js');

            // Use the unrestricted analyzer
            const aiResult = await analyzeScannedImageWithGemini(
                driveFile.driveFileLink || driveFile.webViewLink,
                filename,
                apiKey,
                token
            );

            if (aiResult.success && aiResult.analysis) {
                return {
                    id: driveFile.id,
                    filename: filename,
                    documentType: aiResult.analysis.tipo_documento || 'document',
                    date: aiResult.analysis.fecha_documento ? new Date(aiResult.analysis.fecha_documento) : new Date(),
                    dateSource: 'ai_analysis',
                    confidence: aiResult.analysis.fecha_confianza || 90,
                    needsReview: aiResult.analysis.requiere_revision_humana || false,
                    driveLink: driveFile.driveFileLink || driveFile.webViewLink,
                    metadata: {
                        originalFilename: filename,
                        analyzedAt: new Date(),
                        autoClassified: true,
                        aiSummary: aiResult.analysis.resumen,
                        aiData: aiResult.analysis
                    }
                };
            }
        } catch (e) {
            console.warn("Fallo análisis profundo en analyzer básico, usando fallback:", e);
        }
    }

    // 2. FALLBACK: METADATA REGEX (Original Logic)
    // Extraer fecha del nombre
    const extractedDate = extractDateFromFilename(filename);

    // Usar fecha de creación del archivo como fallback
    const fallbackDate = driveFile.createdTime
        ? new Date(driveFile.createdTime)
        : new Date();

    const finalDate = extractedDate || fallbackDate;

    // Clasificar tipo
    const documentType = classifyDocumentType(filename);
    const confidence = getClassificationConfidence(filename, documentType);

    return {
        id: driveFile.id,
        filename: filename,
        documentType: documentType,
        date: finalDate,
        dateSource: extractedDate ? 'filename' : 'upload',
        confidence: confidence,
        needsReview: confidence < 70 || !extractedDate,
        driveLink: driveFile.driveFileLink || driveFile.webViewLink,
        metadata: {
            originalFilename: filename,
            analyzedAt: new Date(),
            autoClassified: true,
            aiSummary: 'Análisis básico por nombre de archivo (Sin IA profunda)',
            aiData: null
        }
    };
}

/**
 * Analiza batch de documentos y los ordena por fecha
 */
export async function analyzeBatchDocuments(driveFiles, apiKey = null, token = null, sortBy = 'date', sortOrder = 'asc') {
    // Process in parallel with Promise.all
    const analyzed = await Promise.all(driveFiles.map(async file => {
        try {
            return await analyzeDocument(file, apiKey, token);
        } catch (error) {
            console.error(`Error analyzing file ${file.name}:`, error);
            // Return a safe fallback object to avoid crashing the batch
            return {
                ...file,
                analysisStatus: 'error',
                error: error.message,
                documentType: 'Desconocido',
                date: new Date(),
                confidence: 0
            };
        }
    }));

    // Ordenar según criterio
    analyzed.sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'date':
                comparison = new Date(a.date) - new Date(b.date);
                break;
            case 'type':
                comparison = (a.documentType || '').localeCompare(b.documentType || '');
                break;
            case 'confidence':
                comparison = (b.confidence || 0) - (a.confidence || 0); // Mayor confianza primero
                break;
            case 'name':
                comparison = (a.filename || '').localeCompare(b.filename || '');
                break;
        }

        return sortOrder === 'desc' ? -comparison : comparison;
    });

    return analyzed;
}

/**
 * Genera estadísticas del análisis
 */
export function getAnalysisStats(analyzedDocs) {
    const stats = {
        total: analyzedDocs.length,
        byType: {},
        needsReview: 0,
        avgConfidence: 0,
        datesSources: {
            filename: 0,
            upload: 0
        }
    };

    let totalConfidence = 0;

    analyzedDocs.forEach(doc => {
        // Contar por tipo
        stats.byType[doc.documentType] = (stats.byType[doc.documentType] || 0) + 1;

        // Contar necesidad de revisión
        if (doc.needsReview) stats.needsReview++;

        // Confianza promedio
        totalConfidence += doc.confidence;

        // Fuente de fecha
        stats.datesSources[doc.dateSource]++;
    });

    stats.avgConfidence = Math.round(totalConfidence / analyzedDocs.length);

    return stats;
}

/**
 * Mapper de tipos a iconos de timeline
 */
export function getTimelineTypeFromDocType(docType) {
    const mapping = {
        'demanda': 'demanda',
        'sentencia': 'sentencia',
        'resolucion': 'document',
        'notificacion': 'notificacion',
        'prueba': 'prueba',
        'audiencia': 'audiencia',
        'escrito': 'document',
        'apelacion': 'document',
        'hecho_generador': 'hecho_generador'
    };

    return mapping[docType] || 'document';
}

/**
 * Mapper de tipos a colores
 */
export function getColorFromDocType(docType) {
    const colors = {
        'demanda': '#ef4444',
        'sentencia': '#facc15',
        'resolucion': '#60a5fa',
        'notificacion': '#3b82f6',
        'prueba': '#10b981',
        'audiencia': '#f97316',
        'hecho_generador': '#c5a059',
        'document': '#60a5fa'
    };

    return colors[docType] || '#60a5fa';
}
